import { useCallback, useEffect, useState } from 'react'

import { cacheRead, cacheWrite } from './sessionCache'

/**
 * A cursor-paged list that APPENDS.
 *
 * `useResource` replaces `data` on every read, which is right for a single
 * resource and wrong for a page: "load more" has to add to what is already on
 * screen. Rather than teach one hook both behaviours, this is the second one.
 *
 * ── `isComplete` IS THE POINT ───────────────────────────────────────────────
 *
 * §4.5: state the loaded range, and **do not label the first page as all
 * history**. `next_cursor === null` is the only thing that says a list is whole,
 * and a screen that cannot tell the difference will say "3 recordings" when it
 * means "3 so far". The 1.8 approval queue already does this correctly — its
 * badge reports nothing at all while a cursor remains — and that is the rule
 * being generalised here rather than reinvented.
 */

export interface Page<T> {
  items: T[]
  next_cursor?: string | null
}

export interface PagedList<T> {
  items: T[]
  /** The FIRST page. A later page is `isPaging`, so the list is not blanked. */
  isLoading: boolean
  isPaging: boolean
  error: unknown
  nextCursor: string | null
  /** Every page has been fetched. Until this is true, `items` is a prefix. */
  isComplete: boolean
  /**
   * The auto-load budget ran out with pages still unread. **Not the same as
   * `isComplete === false`**, which is also true while a user simply has not
   * pressed "load more" — this one says the list stopped reading on its own.
   */
  budgetSpent: boolean
  pagesLoaded: number
  loadMore: () => void
  reload: () => void
}

export interface PagedListOptions {
  /**
   * How many pages to fetch without being asked. Default 1 — the first page,
   * and nothing else until the user presses "load more".
   *
   * Today needs more than that: `GET /users/{id}/recordings` pages by
   * `created_at` while "today's result" is the newest `session_date`, so the
   * answer is not necessarily on page one and the screen cannot ask a user to
   * page until their own result appears. A BUDGET rather than "until found",
   * because a maximum over an unsorted key cannot stop early — and because
   * §4.5 asks for the requests to stay bounded as pages load.
   */
  autoLoadPages?: number
  /**
   * Opt in to the session cache (Home, owner instruction 2026-10-01; see
   * `sessionCache.ts`). With an answer under this key the list starts settled
   * on the auto-loaded pages it last read and reads them again quietly; an
   * equal answer keeps the same rows array. Pages from "load more" are not
   * cached. `null` opts out.
   */
  cacheKey?: string | null
}

/** What the cache holds for one list: the auto-loaded prefix and its cursor. */
interface CachedPages<T> {
  items: T[]
  nextCursor: string | null
  pagesLoaded: number
  budgetSpent: boolean
}

export function usePagedList<T>(
  fetchPage: (cursor: string | null, signal: AbortSignal) => Promise<Page<T>>,
  { autoLoadPages = 1, cacheKey = null }: PagedListOptions = {},
): PagedList<T> {
  const [cached] = useState(() => cacheRead<CachedPages<T>>(cacheKey))
  const [items, setItems] = useState<T[]>(() => cached?.items ?? [])
  const [nextCursor, setNextCursor] = useState<string | null>(() => cached?.nextCursor ?? null)
  const [pagesLoaded, setPagesLoaded] = useState(() => cached?.pagesLoaded ?? 0)
  const [isLoading, setIsLoading] = useState(() => cached === undefined)
  /**
   * THE PAGE IN FLIGHT, AS STATE RATHER THAN AS A BOOLEAN BESIDE ONE.
   *
   * `isPaging` used to be a flag that `loadMore` set and an inline async
   * function cleared, which made `loadMore` the second place deciding when a
   * page is in flight -- the exact arrangement the comment on the effect below
   * rejects for the auto-load loop. Holding the REQUEST instead collapses them:
   * there is one owner, the effect, and `isPaging` is derived from it.
   */
  const [pageRequest, setPageRequest] = useState<{ cursor: string } | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [generation, setGeneration] = useState(0)
  const [budgetSpent, setBudgetSpent] = useState(() => cached?.budgetSpent ?? false)

  // The first page, and every reload. A changed `fetchPage` -- a different user,
  // a different condition filter -- starts over rather than appending a second
  // user's rows onto the first one's.
  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    void (async () => {
      try {
        // The auto-load loop lives HERE, in the same async flow that already
        // owns the fetching, rather than in an effect that calls `loadMore`.
        // An effect driving another hook's setter is what
        // `react-hooks/set-state-in-effect` is pointing at, and it is also a
        // worse design: two places would decide when a page is in flight.
        let cursor: string | null = null
        const collected: T[] = []
        let pages = 0
        let spent = false
        // A cached list is already on screen: the refresh publishes once, at the
        // end, rather than replacing the rows with a shorter prefix per page.
        const quiet = cacheRead(cacheKey) !== undefined
        // The array on screen, so the cache holds that very object.
        let published: T[] = []

        for (;;) {
          const page: Page<T> = await fetchPage(cursor, controller.signal)
          if (cancelled) return
          collected.push(...page.items)
          cursor = page.next_cursor ?? null
          pages += 1
          // Published per page, so a long scan fills the screen as it goes
          // rather than after it finishes.
          if (!quiet) {
            published = [...collected]
            setItems(published)
            setNextCursor(cursor)
            setPagesLoaded(pages)
          }
          if (cursor === null) break
          if (pages >= Math.max(1, autoLoadPages)) {
            spent = autoLoadPages > 1
            break
          }
        }
        if (cacheKey !== null) {
          const kept = cacheWrite<CachedPages<T>>(cacheKey, { items: quiet ? [...collected] : published, nextCursor: cursor, pagesLoaded: pages, budgetSpent: spent })
          if (quiet) {
            // Equal to what is on screen → the same references → no re-render.
            setItems(kept.items)
            setNextCursor(kept.nextCursor)
            setPagesLoaded(kept.pagesLoaded)
          }
        }
        setBudgetSpent(spent)
        setError(null)
      } catch (err) {
        if (cancelled || controller.signal.aborted) return
        setError(err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [fetchPage, generation, autoLoadPages, cacheKey])

  /**
   * ── THE GUARD IS THE UPDATER, AND IT IS NOT WHAT STOPS DUPLICATES ───────
   *
   * Two taps on "Load more" both run this callback and both close over the same
   * `nextCursor`, because the first page has not landed to change it. The
   * previous version -- a fetch started inside the handler -- then fetched that
   * cursor twice and appended both answers, so the second "page" was the first
   * one again, duplicated into the user's own history. Criterion 16's
   * "duplicates" by name.
   *
   * **What fixes that is the effect below, not this line.** One effect owning
   * the request means a second committed value tears the first request down
   * before it can land, so two appends of one cursor are unrepresentable however
   * hard the button is pressed. Worth being exact about, because the obvious
   * reading -- "the guard prevents the duplicate" -- is wrong, and a mutation
   * that removes this updater leaves every duplicate test green.
   *
   * What the updater buys is the WASTED ROUND TRIP: without it the second tap
   * commits a new object, which aborts a perfectly good in-flight request and
   * re-issues the identical one. On a connection slow enough for a user to tap
   * twice, that is the connection least able to afford it.
   */
  const loadMore = useCallback(() => {
    if (nextCursor === null) return
    setError(null)
    setPageRequest((current) => (current === null ? { cursor: nextCursor } : current))
  }, [nextCursor])

  /**
   * ── AND THE REQUEST IS AN EFFECT, SO IT CAN BE TORN DOWN ────────────────
   *
   * Three faults follow from a fetch started inside an event handler, and all
   * three were live until History became the first caller:
   *
   * 1. **The signal was `new AbortController().signal`** -- a controller built
   *    inline, held by nobody and therefore aborted by nothing. Every other read
   *    in this app aborts on unmount; this one ran to completion after sign-out,
   *    against a token that was still valid.
   * 2. **Nothing checked `cancelled` after the await.** React 19 does not warn on
   *    a post-unmount `setState`, so nothing would have said so.
   * 3. **A changed `fetchPage` did not abandon it.** The render-time reset below
   *    empties `items` for the new query, and the old query's page then landed
   *    and appended onto it. `fetchPage` is keyed on the user id, so that is one
   *    account's recordings on another account's screen -- §7.4, not cosmetics.
   *
   * An effect fixes all three at once because it has a cleanup, and `fetchPage`
   * in its dependency list is what makes a changed query tear the request down
   * rather than race it.
   */
  useEffect(() => {
    if (pageRequest === null) return
    const controller = new AbortController()
    let cancelled = false

    void (async () => {
      try {
        const page = await fetchPage(pageRequest.cursor, controller.signal)
        if (cancelled) return
        setItems((previous) => [...previous, ...page.items])
        setNextCursor(page.next_cursor ?? null)
        setPagesLoaded((n) => n + 1)
        setError(null)
      } catch (err) {
        if (cancelled || controller.signal.aborted) return
        // The rows already on screen stay. A failed "load more" must not empty
        // a list the user was reading.
        setError(err)
      } finally {
        // Not when cancelled: the reset that superseded this request has already
        // cleared it, and clearing it again would re-run this effect against a
        // cursor belonging to a query nobody is showing.
        if (!cancelled) setPageRequest(null)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [pageRequest, fetchPage])

  const reload = useCallback(() => {
    setItems([])
    setNextCursor(null)
    setPagesLoaded(0)
    // A page still in flight belongs to the read being discarded. Clearing the
    // request tears its effect down, so it cannot land on top of the new one.
    setPageRequest(null)
    setGeneration((n) => n + 1)
  }, [])

  const [lastGeneration, setLastGeneration] = useState(generation)
  if (lastGeneration !== generation) {
    setLastGeneration(generation)
    setIsLoading(true)
  }

  /**
   * A CHANGED `fetchPage` IS A DIFFERENT LIST.
   *
   * The effect below already restarts on one -- its own comment says a different
   * user or a different condition filter "starts over rather than appending a
   * second user's rows onto the first one's". But until it resolves, `items`,
   * `nextCursor`, `pagesLoaded` and `isComplete` still describe the OLD query,
   * and `isLoading` is false. So the hook reports a settled, complete list
   * belonging to the previous input, and a caller renders it under the new
   * input's heading.
   *
   * The same defect lived in `useResource`, where it put one condition's baseline
   * under the other's name. Here it would let History show the EC rows under an
   * EO filter and, worse, let `isComplete` claim that partial view is whole --
   * which is the one thing §4.5 forbids outright.
   *
   * A render-time adjustment rather than an effect, for the reason the rest of
   * this codebase uses one: an effect runs a frame later, and a frame is long
   * enough to paint the wrong list. The wrapper object is because `useState(fn)`
   * treats a function as a lazy initialiser.
   */
  const [lastFetch, setLastFetch] = useState<{ fn: typeof fetchPage }>({ fn: fetchPage })
  if (lastFetch.fn !== fetchPage) {
    setLastFetch({ fn: fetchPage })
    // The NEW list's own cached pages, when it has them: never the old list's.
    const hit = cacheRead<CachedPages<T>>(cacheKey)
    setItems(hit?.items ?? [])
    setNextCursor(hit?.nextCursor ?? null)
    setPagesLoaded(hit?.pagesLoaded ?? 0)
    setBudgetSpent(hit?.budgetSpent ?? false)
    setError(null)
    setIsLoading(hit === undefined)
    // The old query's "load more" is abandoned here as well as torn down by the
    // effect's dependency on `fetchPage`. Both are needed: the teardown stops
    // the request, and this stops the effect immediately re-firing the PREVIOUS
    // cursor against the NEW query.
    setPageRequest(null)
  }

  return {
    items,
    isLoading,
    // Derived, not stored. A second copy would be one more thing to keep in step,
    // and setting it inside the effect is what `react-hooks/set-state-in-effect`
    // exists to refuse.
    isPaging: pageRequest !== null,
    error,
    nextCursor,
    isComplete: pagesLoaded > 0 && nextCursor === null,
    budgetSpent,
    pagesLoaded,
    loadMore,
    reload,
  }
}
